import React, { useState } from 'react';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Badge,
  Flex,
  Input,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useDisclosure,
  Grid,
  GridItem,
  Divider,
  IconButton,
  useToast,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon, DeleteIcon, DownloadIcon } from '@chakra-ui/icons';
import PageHeader from './shared/PageHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ImageViewerModal from './shared/ImageViewerModal';
import StorageImage from './shared/StorageImage';
import ContainerVanTypeahead from './ContainerVanTypeahead';
import { 
  getContainerVanChecklists, 
  deleteContainerVanChecklist,
  getStorageUrl,
  generateContainerVanPDFSummary
} from '../utils/containerVanChecklistUtils';
import { exportChecklistResultsToPDF } from '../utils/containerVanChecklistExport';
import { exportAllContainerVansToPDF } from '../utils/containerVanFleetExport';
import type { ContainerVanChecklistData, ContainerVan } from '../types/containerVan';

const ContainerVanChecklistMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany, hasFeature, isSuperAdmin  } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isImageModalOpen, 
    onOpen: onImageModalOpen, 
    onClose: onImageModalClose 
  } = useDisclosure();
  
  const [checklists, setChecklists] = useState<ContainerVanChecklistData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<ContainerVanChecklistData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [selectedImageTitle, setSelectedImageTitle] = useState<string>('');
  const [exportLoading, setExportLoading] = useState<'results' | 'fleet' | 'checklist' | null>(null);
  
  // Search filters
  const today = new Date().toISOString().split('T')[0];
  const [searchFilters, setSearchFilters] = useState({
    createdFrom: today,
    createdTo: today
  });
  const [selectedVan, setSelectedVan] = useState<ContainerVan | null>(null);
  const [vanSearchQuery, setVanSearchQuery] = useState('');

  // No auto-loading - wait for user to search

  const handleSearch = async () => {
    if (!selectedCompany?.companyId) {
      toast({
        title: 'Error',
        description: 'No company selected',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Require at least date range
    if (!searchFilters.createdFrom || !searchFilters.createdTo) {
      toast({
        title: 'Search Required',
        description: 'Please select a date range to search',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      
      // Build Firestore query with filters
      const searchParams = {
        companyId: selectedCompany.companyId,
        createdFrom: searchFilters.createdFrom,
        createdTo: searchFilters.createdTo,
        vanNumber: selectedVan?.vanNumber || (vanSearchQuery.trim() || undefined)
      };
      
      const checklistData = await getContainerVanChecklists(
        selectedCompany.companyId,
        searchFilters.createdFrom,
        searchFilters.createdTo
      );
      
      // Apply client-side filtering for van number if provided
      let filteredData = checklistData;
      if (searchParams.vanNumber) {
        filteredData = checklistData.filter(checklist => 
          checklist.vanNumber.toLowerCase().includes(searchParams.vanNumber!.toLowerCase())
        );
      }
      
      setChecklists(filteredData);
      
      toast({
        title: 'Search Complete',
        description: `Found ${filteredData.length} inspection records`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error searching checklists:', error);
      toast({
        title: 'Search Failed',
        description: 'Failed to search checklists',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    const today = new Date().toISOString().split('T')[0];
    setSearchFilters({
      createdFrom: today,
      createdTo: today
    });
    setSelectedVan(null);
    setVanSearchQuery('');
    setChecklists([]);
    setHasSearched(false);
  };

  const handleViewChecklist = (checklist: ContainerVanChecklistData) => {
    setSelectedChecklist(checklist);
    onOpen();
  };

  const handleViewImage = async (storagePath: string, title: string = 'Image') => {
    try {
      const imageUrl = await getStorageUrl(storagePath);
      setSelectedImageUrl(imageUrl);
      setSelectedImageTitle(title);
      onImageModalOpen();
    } catch (error) {
      console.error('Error loading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to load image',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteChecklist = async (checklistId: string) => {
    try {
      setDeleteLoading(checklistId);
      await deleteContainerVanChecklist(checklistId);
      
      // Update local state
      const updatedChecklists = checklists.filter(c => c.id !== checklistId);
      setChecklists(updatedChecklists);
      
      toast({
        title: 'Success',
        description: 'Checklist deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete checklist',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleExportResults = async () => {
    if (!selectedCompany?.companyName) {
      toast({
        title: 'Error',
        description: 'No company selected',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setExportLoading('results');
      
      toast({
        title: 'Exporting...',
        description: 'Generating PDF export of search results',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      await exportChecklistResultsToPDF(
        checklists,
        selectedCompany.companyName,
        searchFilters
      );

      toast({
        title: 'Export Complete',
        description: `Successfully exported ${checklists.length} inspection records`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting results:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export results',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportFleet = async () => {
    if (!selectedCompany?.companyId || !selectedCompany?.companyName) {
      toast({
        title: 'Error',
        description: 'No company selected',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setExportLoading('fleet');
      
      toast({
        title: 'Exporting...',
        description: 'Generating comprehensive fleet report. This may take a moment for large fleets.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      await exportAllContainerVansToPDF(
        selectedCompany.companyId,
        selectedCompany.companyName
      );

      toast({
        title: 'Export Complete',
        description: 'Successfully exported complete fleet report',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting fleet:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export fleet report',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportChecklistToPDF = async (checklist: ContainerVanChecklistData) => {
    try {
      setExportLoading('checklist');
      
      toast({
        title: 'Exporting PDF...',
        description: 'Generating inspection report PDF',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      // Calculate total and completed items from categories
      let totalRequired = 0;
      let completedRequired = 0;
      
      checklist.categories.forEach(category => {
        category.items.forEach(item => {
          if (item.required) {
            totalRequired++;
            if (item.value !== undefined && item.value !== null && item.value !== '') {
              completedRequired++;
            }
          }
        });
      });

      // Prepare PDF generation parameters
      const pdfParams = {
        vanNumber: checklist.vanNumber,
        genSetNumber: checklist.genSetNumber || 'N/A',
        location: checklist.location || 'N/A',
        companyName: selectedCompany?.companyName || 'Unknown Company',
        categories: checklist.categories || [],
        totalRequired: totalRequired,
        completedRequired: completedRequired,
        isFullyComplete: checklist.isComplete || false,
        overallStatus: checklist.overallStatus,
        inspectionSummary: checklist.inspectionSummary,
        signatureDataURL: checklist.signatureDataURL,
        inspectorName: checklist.created_by || 'Unknown Inspector',
        checklistType: 'Container Van Inspection',
        genSetRemainingHours: checklist.genSetRemainingHours
      };

      const filename = await generateContainerVanPDFSummary(pdfParams);

      toast({
        title: 'Export Complete',
        description: `PDF saved as: ${filename}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting checklist to PDF:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export PDF',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportLoading(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    let date: Date;
    if (timestamp.seconds) {
      // Firestore Timestamp
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp.toDate) {
      // Firestore Timestamp object
      date = timestamp.toDate();
    } else {
      // Regular Date
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getOverallStatusColor = (overallStatus: string) => {
    switch (overallStatus) {
      case 'pass':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'fail':
        return 'red';
      default:
        return 'gray';
    }
  };

  const renderChecklistDetails = (checklist: ContainerVanChecklistData) => {
    if (!checklist) return null;

    return (
      <VStack spacing={6} align="stretch">
        {/* Basic Information */}
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="lg" fontWeight="bold">Basic Information</Text>
            <Badge colorScheme={getOverallStatusColor(checklist.overallStatus)} fontSize="md" px={3} py={1}>
              {checklist.overallStatus.toUpperCase()}
            </Badge>
          </Flex>
          
          <VStack align="start" spacing={3}>
            <Text fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="medium">Company:</Text> {selectedCompany?.companyName || 'N/A'}
            </Text>
            <Text fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="medium">Van #:</Text> {checklist.vanNumber}
            </Text>
            <Text fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="medium">GenSet #:</Text> {checklist.genSetNumber}
            </Text>
            <Text fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="medium">Location:</Text> {checklist.location}
            </Text>
          </VStack>
        </Box>

        <Divider />

        {/* Inspection Results */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>Inspection Results</Text>
          <VStack spacing={4} align="stretch">
            {checklist.categories && checklist.categories.map((category, categoryIndex) => (
              <Box key={categoryIndex} bg="gray.50" p={4} borderRadius="md">
                <HStack justify="space-between" align="start" mb={3}>
                  <Text fontWeight="bold" color="teal.600" fontSize="md">
                    {category.name}
                  </Text>
                  {/* Category Photo */}
                  {category.photoUrl && (
                    <StorageImage
                      storagePath={category.photoUrl}
                      alt={`${category.name} photo`}
                      boxSize="48px"
                      objectFit="cover"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      cursor="pointer"
                      onClick={() => handleViewImage(category.photoUrl!, `${category.name} Photo`)}
                      _hover={{ opacity: 0.8 }}
                    />
                  )}
                </HStack>
                
                <VStack spacing={3} align="stretch">
                  {category.items && category.items.map((item, itemIndex) => (
                    <Box key={itemIndex} bg="white" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                      <HStack justify="space-between" align="start" mb={2}>
                        <Text fontSize="sm" fontWeight="medium" flex={1}>{item.text}</Text>
                        <Box>
                          {item.type === 'slider' || item.type === 'gauge' ? (
                            <Text fontSize="sm" fontWeight="medium" color="blue.600">
                              {item.value || 'N/A'}{item.unit || ''}
                            </Text>
                          ) : item.type === 'checkbox' ? (
                            <Badge colorScheme={item.value ? 'green' : 'red'}>
                              {item.value ? 'Pass' : 'Fail'}
                            </Badge>
                          ) : (
                            <Text fontSize="sm" fontWeight="medium">{item.value || 'N/A'}</Text>
                          )}
                        </Box>
                      </HStack>
                      
                      {/* Item Notes */}
                      {item.notes && (
                        <Text fontSize="xs" color="gray.600" bg="gray.50" p={2} borderRadius="md" mt={2}>
                          <Text as="span" fontWeight="medium">Notes:</Text> {item.notes}
                        </Text>
                      )}
                      
                      {/* Item Photos */}
                      {item.photos && item.photos.length > 0 && (
                        <HStack mt={2} spacing={2}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.600">Photos:</Text>
                          {item.photos.map((photoPath, photoIndex) => (
                            <StorageImage
                              key={photoIndex}
                              storagePath={photoPath}
                              alt={`${item.text} photo ${photoIndex + 1}`}
                              boxSize="48px"
                              objectFit="cover"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                              cursor="pointer"
                              onClick={() => handleViewImage(photoPath, `${item.text} Photo ${photoIndex + 1}`)}
                              _hover={{ opacity: 0.8 }}
                            />
                          ))}
                        </HStack>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>

        <Divider />

        {/* Inspection Summary */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>Inspection Summary</Text>
          <VStack spacing={3} align="stretch">
            {/* GenSet Remaining Operating Hours */}
            {checklist.genSetRemainingHours !== undefined && (
              <Box bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                <Text fontSize="sm" color="blue.800">
                  <Text as="span" fontWeight="bold">GenSet Remaining Operating Hours:</Text>{' '}
                  {checklist.genSetRemainingHours > 0 
                    ? `${checklist.genSetRemainingHours.toFixed(1)} hours`
                    : 'Unable to calculate'
                  }
                </Text>
              </Box>
            )}
            
            {/* Summary Notes */}
            {checklist.inspectionSummary && (
              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.700">
                  <Text as="span" fontWeight="medium">Summary:</Text>
                </Text>
                <Text fontSize="sm" mt={1}>{checklist.inspectionSummary}</Text>
              </Box>
            )}
          </VStack>
        </Box>

        {/* Signature */}
        {checklist.signatureDataURL && (
          <>
            <Divider />
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3}>Inspector Signature</Text>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                <img 
                  src={checklist.signatureDataURL} 
                  alt="Inspector Signature" 
                  style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                />
              </Box>
            </Box>
          </>
        )}
        {/* Inspector and Date Information */}
        <Text fontSize="sm" color="gray.700">
          <Text as="span" fontWeight="medium">Inspector:</Text> {checklist.created_by || 'N/A'}
        </Text>
        <Text fontSize="sm" color="gray.700">
          <Text as="span" fontWeight="medium">Inspected:</Text> {formatDate(checklist.created_at)}
        </Text>
      </VStack>
    );
  };

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      <PageHeader
        title="Checklist Monitoring"
        subtitle="View & Track Container Van Inspections"
        icon="📦"
        onBack={() => navigate('/container-van-checklist')}
      />

      <Container maxW="6xl" py={12} px={4}>
        {/* Search Section */}
        <Card
          mb={6}
          bg="rgba(255, 255, 255, 0.8)"
          backdropFilter="blur(20px)"
          borderRadius="2xl"
          boxShadow="0 8px 32px rgba(0,0,0,0.12)"
          border="1px solid"
          borderColor="whiteAlpha.500"
        >
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="700" bgGradient="linear(135deg, gray.700, gray.900)" bgClip="text">
                Search Filters
              </Text>
              
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Created From</FormLabel>
                    <Input
                      type="date"
                      value={searchFilters.createdFrom}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, createdFrom: e.target.value }))}
                      bg="gray.50"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: 'teal.400',
                        boxShadow: '0 0 0 1px teal.400',
                        bg: 'white'
                      }}
                    />
                  </FormControl>
                </GridItem>
                
                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Created To</FormLabel>
                    <Input
                      type="date"
                      value={searchFilters.createdTo}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, createdTo: e.target.value }))}
                      bg="gray.50"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: 'teal.400',
                        boxShadow: '0 0 0 1px teal.400',
                        bg: 'white'
                      }}
                    />
                  </FormControl>
                </GridItem>
                
                <GridItem>
                  <FormControl position="relative">
                    <FormLabel fontSize="sm" color="gray.600">Container Van <Text as="span" color="gray.400">(optional)</Text></FormLabel>
                    <ContainerVanTypeahead
                      value={vanSearchQuery}
                      onChange={(query) => {
                        setVanSearchQuery(query);
                        // If user clears the search, clear the selected van
                        if (!query.trim()) {
                          setSelectedVan(null);
                        }
                      }}
                      onSelect={(van) => {
                        setSelectedVan(van);
                        setVanSearchQuery(van.vanNumber);
                      }}
                      placeholder="Search for a container van..."
                      companyId={selectedCompany?.companyId}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
              
              <HStack spacing={3} pt={2}>
                {(hasFeature("container_van_checklist.monitor.search", selectedCompany?.companyId) || isSuperAdmin) ? (
                  <>
                    <Button
                      colorScheme="teal"
                      leftIcon={<SearchIcon />}
                      onClick={handleSearch}
                      flex={{ base: 1, md: "auto" }}
                      isLoading={loading}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleClearSearch}
                      flex={{ base: 1, md: "auto" }}
                      isDisabled={loading}
                    >
                      Clear
                    </Button>
                  </>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">Search feature not available for your subscription plan.</Text>
                  </Alert>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Export Buttons */}
        {(hasFeature("container_van_checklist.monitor.exportPDF", selectedCompany?.companyId) || isSuperAdmin) &&
        <Card bg="white" shadow="sm">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold" color="teal.600">
                Export Options
              </Text>
              
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <GridItem>
                  <Button
                    colorScheme="blue"
                    leftIcon={<DownloadIcon />}
                    onClick={handleExportResults}
                    isLoading={exportLoading === 'results'}
                    loadingText="Exporting..."
                    isDisabled={checklists.length === 0 || exportLoading !== null}
                    w="100%"
                    size="md"
                  >
                    Export Search Results to PDF
                  </Button>
                  <Text fontSize="xs" color="gray.500" mt={1} textAlign="center">
                    Export {checklists.length} filtered inspection{checklists.length !== 1 ? 's' : ''}
                  </Text>
                </GridItem>
                
                <GridItem>
                  <Button
                    colorScheme="purple"
                    leftIcon={<DownloadIcon />}
                    onClick={handleExportFleet}
                    isLoading={exportLoading === 'fleet'}
                    loadingText="Exporting..."
                    isDisabled={!selectedCompany?.companyId || exportLoading !== null}
                    w="100%"
                    size="md"
                  >
                    Export All Vans to PDF
                  </Button>
                  <Text fontSize="xs" color="gray.500" mt={1} textAlign="center">
                    Export complete fleet report for {selectedCompany?.companyName || 'company'}
                  </Text>
                </GridItem>
              </Grid>
            </VStack>
          </CardBody>
        </Card>
        }

      {/* Content */}
      {loading ? (
        <Box textAlign="center" py={8}>
          <Spinner size="lg" color="teal.500" />
          <Text mt={4} color="gray.600">Searching checklists...</Text>
        </Box>
      ) : !hasSearched ? (
        <Alert status="info">
          <AlertIcon />
          Please select a date range and click Search to view inspection records.
        </Alert>
      ) : checklists.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No checklists found matching your search criteria.
        </Alert>
      ) : (
        <VStack spacing={4} align="stretch">
          {checklists.map((checklist) => (
            <Card key={checklist.id} variant="outline" bg="white">
              <CardBody>
                {/* Header: Van Number with Action Icons */}
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="bold" fontSize="lg" color="teal.600">
                    {checklist.vanNumber} - <Badge colorScheme={getOverallStatusColor(checklist.overallStatus)} fontSize="md" px={3} py={1}>
                      {checklist.overallStatus.toUpperCase()}
                    </Badge>
                  </Text>
                  <HStack>
                    {(hasFeature("container_van_checklist.monitor.viewChecklist", selectedCompany?.companyId) || isSuperAdmin) &&
                    <IconButton
                      aria-label="View checklist"
                      icon={<ViewIcon />}
                      colorScheme="teal"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewChecklist(checklist)}
                    />
                    }
                    {(hasFeature("container_van_checklist.monitor.deleteChecklist", selectedCompany?.companyId) || isSuperAdmin) &&
                    <IconButton
                      aria-label="Delete checklist"
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      isLoading={deleteLoading === checklist.id}
                      onClick={() => handleDeleteChecklist(checklist.id!)}
                    />
                    }
                  </HStack>
                </Flex>

                {/* Content: GenSet, Inspector, Date */}
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color="gray.700">
                    <Text as="span" fontWeight="medium">GenSet #:</Text> {checklist.genSetNumber}
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    <Text as="span" fontWeight="medium">Inspector:</Text> {checklist.created_by || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    <Text as="span" fontWeight="medium">Inspected:</Text> {formatDate(checklist.created_at)}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}

      {/* View Checklist Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Checklist Details - {selectedChecklist?.vanNumber}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {renderChecklistDetails(selectedChecklist!)}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3} width="100%" justifyContent="space-between">
              <Button
                leftIcon={<DownloadIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={() => handleExportChecklistToPDF(selectedChecklist!)}
                isLoading={exportLoading === 'checklist'}
                loadingText="Exporting..."
                isDisabled={!selectedChecklist || exportLoading !== null}
              >
                Export to PDF
              </Button>
              <Button onClick={onClose}>Close</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={isImageModalOpen}
        onClose={onImageModalClose}
        imageUrl={selectedImageUrl}
        title={selectedImageTitle}
      />
      </Container>
    </Box>
  );
};

export default ContainerVanChecklistMonitoring;